/**
 * API client dla embed-komponentów akceptacji na karcie rekordu (F17 T17.1.7).
 *
 * Oddzielony od `approvals-api.ts` (tamten = 3 listingi w module Procesy).
 * Tutaj: detal pojedynczej instancji + akcje (start / decyzja / anuluj) +
 * lista procesów dostępnych dla modułu (dla SubmitForApprovalButton).
 *
 * Endpointy:
 *   - `GET  /api/processes?targetModuleCode=&isPublished=true&isActive=true`
 *   - `GET  /api/processes/instances?moduleCode=&recordId=`
 *   - `GET  /api/processes/instances/:id`            (detal — T17.1.7 backend)
 *   - `POST /api/processes/:id/start`
 *   - `POST /api/processes/decisions/:stepInstanceId`
 *   - `POST /api/processes/instances/:id/cancel`
 */

import type {
  AvailableRoute,
  DecisionPriority,
  ProcessDecisionSystemReason,
  RecordProcessPermissions,
} from '@saas/shared';

import type { ApproverConfigValue } from '@/components/process-builder/ApproverPicker';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

import type { InstanceListingRow, InstanceStatus } from './approvals-api';

export type { InstanceStatus } from './approvals-api';

/**
 * Wiersz listingu instancji rekordu — zwracany przez `GET /api/processes/instances?moduleCode=&recordId=`
 * (embed na karcie rekordu, patrz `fetchInstancesForRecord`). W odróżnieniu od trzech listingów modułu
 * Procesy (`approvals-api.ts` — Aktywne/Moje decyzje/Moje wysłane) ten endpoint NIE dołącza
 * `targetLabel` (etykieta rekordu docelowego jest zbędna — strona JUŻ jest na tym rekordzie).
 * `Omit` zamiast osobnej definicji — reszta pól 1:1 z `InstanceListingRow` (zero dryfu przy zmianie enrichmentu).
 */
export type RecordInstanceListingRow = Omit<InstanceListingRow, 'targetLabel'>;

/**
 * Odpowiedź listingu instancji rekordu: wiersze + prawa obiegu w firmie rekordu
 * (`RecordProcessPermissions` z `@saas/shared`, decyzja właściciela 2026-10-08, „firma z rekordu,
 * nie ze sprawy"). `recordPermissions` = `null`, gdy serwer ich nie podał (starsza wersja API) —
 * brak wiedzy, nie zgoda: przyciski obiegu zostają wtedy ukryte (fail-closed).
 */
export interface RecordInstancesResult {
  rows: RecordInstanceListingRow[];
  recordPermissions: RecordProcessPermissions | null;
}

// ============================================================================
// Typy współdzielone z detalem instancji
// ============================================================================

/**
 * Typy decyzji wysyłane przez panel (zgodne z walidatorem `POST /processes/decisions/:id`).
 * Odrzucenie, zwrot i przekazanie to nie osobne akcje, tylko DROGI narysowane w procesie
 * (`approve` + `routeKey`). Wyjątek: `step_back` — „Cofnij do poprzedniego etapu", osobna akcja
 * etapu (nie droga): wraca sprawę do osób z poprzedniego etapu, z obowiązkowym komentarzem.
 */
export type DecisionType = 'approve' | 'comment' | 'step_back';

/** Status kroku (process_step_instances). */
export type StepStatus = 'pending' | 'in_progress' | 'done' | 'skipped' | 'error' | 'timed_out';

/** Węzeł z zamrożonego snapshotu (id + label + typ wystarczają dla pickera/tabeli). */
export interface SnapshotNode {
  id: string;
  label?: string | null;
  nodeType?: string;
  /**
   * F.4 (ADR-0073) — konfiguracja akceptantów węzła, w tym `requireAddresseeDesignation`
   * / `defaultAddresseeUserId`. Backend NIE filtruje pól węzła w projekcji snapshotu
   * (`GET /api/processes/instances/:id` zwraca `snapshot.nodes` 1:1 z zapisanego JSONB),
   * więc to pole jest już na wire — dotąd po prostu niewpisane w ten typ.
   */
  approverConfig?: ApproverConfigValue;
  /** Pozycja węzła w kreatorze w chwili startu sprawy (diagram); `null` = układ automatyczny. */
  positionX?: number | null;
  positionY?: number | null;
  /**
   * Autor procesu pozwolił na tym etapie „Cofnij do poprzedniego etapu". Brak klucza = `false`
   * (migawki sprzed tej funkcji). Okno decyzji pokazuje opcję dopiero, gdy serwer zwróci
   * `InstanceStep.stepBack` — samej flagi z migawki nie czyta.
   */
  allowStepBack?: boolean;
}

/**
 * Krawędź z zamrożonego snapshotu rzutowana do klienta — historia („<droga>: skąd → dokąd")
 * i diagram. Listę dróg DO WYBORU daje serwer (`InstanceStep.availableRoutes`), bo tylko on
 * liczy warunki dróg warunkowych.
 */
export interface SnapshotEdge {
  id: string;
  sourceNodeId: string;
  edgeType: string;
  label: string | null;
  sortOrder?: number;
  isDefault?: boolean;
  /**
   * F.4 (ADR-0073) — węzeł docelowy krawędzi, potrzebny do wyznaczenia po stronie
   * klienta KTÓRY węzeł zostanie aktywowany po decyzji (żeby wiedzieć czy pokazać
   * picker adresata PRZED wysłaniem decyzji — patrz `resolveAddresseeFields`
   * w `addressee-resolution.ts`). Backend projektuje to pole w
   * `apps/api/app/api/processes/instances/[id]/route.ts`.
   */
  targetNodeId: string;
}

/** Decyzja na kroku (z detalu instancji). */
export interface InstanceDecision {
  id: string;
  /**
   * Świadomie `string`, nie union `DecisionType` — backend (`process_decisions`)
   * zapisuje także decyzje automatyczne (np. `cancelled`, `automated_escalation`,
   * `automated_target_deleted`). Zawężenie do `DecisionType` byłoby regresją (gubiłoby te typy).
   */
  decisionType: string;
  decidedByUserId: string | null;
  decidedAt: string | null;
  /** Komentarz osoby decydującej; gdy wpis ma `systemReason`, komentarz jest `null`. */
  comment: string | null;
  /** Droga wybrana w decyzji — id krawędzi w `snapshot.edges`; `null` dla komentarzy/automatów. */
  routeEdgeId: string | null;
  /**
   * Powód wpisu automatycznego (kod + parametry) — panel tłumaczy go na tekst w języku
   * użytkownika. `null` dla decyzji osób oraz dla najstarszych wpisów automatycznych, które
   * niosą własny komentarz.
   */
  systemReason: ProcessDecisionSystemReason | null;
  onBehalfOfUserId: string | null;
}

/**
 * Cofnięcie z danego kroku do poprzedniego etapu (liczy serwer dla pytającego — tą samą funkcją
 * co silnik przy zapisie). `targetUsers` to osoby, do których wróci sprawa: te, które decydowały
 * na poprzednim etapie, bez niedostępnych (konto nieaktywne albo brak dostępu do firmy rekordu).
 * `blockedReason = 'NO_APPROVERS'` (przy pustym `targetUsers`) = nikt z nich nie jest już dostępny:
 * cofnięcie jest skonfigurowane, ale niemożliwe — okno pokazuje je wtedy jako nieaktywne z powodem.
 */
export interface StepBackInfo {
  targetNodeId: string;
  /** Nazwa poprzedniego etapu; `null`, gdy etap jej nie ma. */
  targetStageLabel: string | null;
  /** Osoby, do których wróci sprawa; `displayName` bywa pusty (kolumna konta jest opcjonalna). */
  targetUsers: Array<{ id: string; displayName: string | null }>;
  blockedReason: 'NO_APPROVERS' | null;
}

/** Krok instancji + jego decyzje (z detalu instancji). */
export interface InstanceStep {
  stepInstanceId: string;
  nodeId: string;
  label: string | null;
  status: StepStatus;
  assignedToUserIds: string[];
  decisions: InstanceDecision[];
  /**
   * Drogi dostępne na tym etapie (liczone przez serwer, z warunkami), w kolejności
   * wyświetlania — domyślna pierwsza. Pusta tablica dla kroków zamkniętych.
   */
  availableRoutes: AvailableRoute[];
  /**
   * Czy zalogowany użytkownik może TERAZ zdecydować na tym kroku (liczy serwer dla pytającego):
   * krok otwarty w sprawie w toku, użytkownik jest w puli akceptantów albo jest przełożonym, do
   * którego eskalowano krok, i jeszcze nie głosował na tym kroku. Baner na karcie rekordu pokazuje
   * przyciski decyzji tylko przy takim kroku. Uprawnienie do decydowania sprawdza dopiero zapis
   * decyzji.
   */
  canDecide: boolean;
  /**
   * Czy głos zalogowanego użytkownika za drogą DOMYŚLNĄ (albo jedyną) zamknie etap i przesunie
   * sprawę dalej (liczy serwer — zna próg głosów etapu). `false`, gdy etap w trybie „wszyscy" /
   * „większość" czeka jeszcze na głosy innych albo użytkownik nie może teraz decydować. Głos za
   * drogą INNĄ niż domyślna zamyka etap zawsze — tego pole nie opisuje. Okno decyzji pokazuje
   * pole „akceptant na następnym etapie" tylko przy głosie, który zamyka etap.
   */
  defaultRouteVoteClosesStage: boolean;
  /**
   * Krok, do którego prowadzi „Cofnij" z tego kroku (poprzedni etap po śladzie); `null` dla kroku
   * pierwszego etapu (po starcie albo w gałęzi równoległej). Historia składa po nim etykietę
   * „Cofnięto: <z> → <do>" — cel to krok o tym `stepInstanceId` na tej samej liście kroków.
   */
  previousStepInstanceId: string | null;
  /**
   * Opcja „Cofnij do poprzedniego etapu" dla pytającego. Niepusta tylko wtedy, gdy może on teraz
   * decydować, autor włączył cofanie na etapie i jest dokąd wracać; w pozostałych przypadkach `null`.
   */
  stepBack: StepBackInfo | null;
}

/** Pełny detal instancji — zwracany przez `GET /api/processes/instances/:id`. */
export interface InstanceDetail {
  instance: {
    id: string;
    instanceNumber: number;
    processId: string;
    targetModuleCode: string;
    targetRecordId: string;
    submittedByUserId: string;
    submittedAt: string;
    status: InstanceStatus;
    currentNodeIds: string[] | null;
    completedAt: string | null;
    companyId: string | null;
    initiatorComment: string | null;
    /** Etap końcowy zakończonej sprawy (id w `snapshot.nodes`); `null` gdy nie zakończona. */
    finalNodeId: string | null;
    /** Nazwa etapu końcowego — pokazywana zamiast „Zatwierdzony" (model WF). */
    finalNodeLabel: string | null;
  };
  snapshot: { nodes: SnapshotNode[]; edges: SnapshotEdge[] };
  steps: InstanceStep[];
  /**
   * Definicja procesu zmieniła się od startu sprawy (etapy, drogi, ich ustawienia — bez
   * samego przesunięcia klocków). Diagram pokazuje wtedy adnotację: sprawa idzie wg wersji
   * ze startu.
   */
  definitionChanged: boolean;
}

/** Skrót definicji procesu — dla pickera w SubmitForApprovalButton. */
export interface ProcessSummary {
  id: string;
  number: number;
  name: string;
  description: string | null;
  targetModuleCode: string;
  isActive: boolean;
  isPublished: boolean;
  version: number;
}

// ============================================================================
// Procesy dostępne dla modułu (opublikowane + aktywne)
// ============================================================================

/**
 * F17/klasyfikatory — filtr po osi klasyfikatora bieżącego rekordu. Dokładnie
 * JEDEN z trzech pól jest przekazywany do serwera na raz (mutual exclusion po
 * stronie `GET /api/processes`); priorytet documentTypeId > categoryId >
 * uncategorized ustala caller (patrz `SubmitForApprovalButton`).
 */
export interface ProcessClassifierFilter {
  documentTypeId?: string | null;
  categoryId?: string | null;
  uncategorized?: boolean;
}

export async function fetchProcessesForModule(
  targetModuleCode: string,
  classifier?: ProcessClassifierFilter,
): Promise<ProcessSummary[]> {
  const params: Record<string, string> = {
    targetModuleCode,
    isPublished: 'true',
    isActive: 'true',
  };
  if (classifier?.documentTypeId) {
    params.documentTypeId = classifier.documentTypeId;
  } else if (classifier?.categoryId) {
    params.categoryId = classifier.categoryId;
  } else if (classifier?.uncategorized) {
    params.uncategorized = 'true';
  }
  const res = await api.get<{ data: ProcessSummary[]; total: number }>('/processes', { params });
  return res.data.data;
}

// ============================================================================
// Instancje dla rekordu (nagłówki) + detal jednej instancji
// ============================================================================

/**
 * Instancje rekordu + prawa do akcji obiegu w firmie rekordu (`recordPermissions`, patrz
 * `RecordInstancesResult`). Jedno żądanie, jeden klucz zapytania (`recordInstancesKey`) dla
 * wszystkich obserwatorów; konsumenci, którym wystarczą same wiersze, biorą je przez
 * `select: selectRecordInstanceRows` (hook `useActiveInstanceForRecord`).
 */
export async function fetchInstancesForRecord(
  moduleCode: string,
  recordId: string,
): Promise<RecordInstancesResult> {
  const res = await api.get<{
    data: RecordInstanceListingRow[];
    total: number;
    // Opcjonalne: starsza wersja API nie zwraca pola — brak = brak wiedzy (fail-closed w panelu).
    recordPermissions?: RecordProcessPermissions;
  }>('/processes/instances', { params: { moduleCode, recordId } });
  return { rows: res.data.data, recordPermissions: res.data.recordPermissions ?? null };
}

export async function fetchInstanceDetail(instanceId: string): Promise<InstanceDetail> {
  const res = await api.get<InstanceDetail>(`/processes/instances/${instanceId}`);
  return res.data;
}

/** Kod odpowiedzi 404 szczegółów sprawy, gdy pytający nie ma do niej dostępu (albo jej nie ma). */
const INSTANCE_NOT_FOUND_CODE = 'INSTANCE_NOT_FOUND';

/**
 * Czy błąd z API niesie kod `INSTANCE_NOT_FOUND` (serwer odpowiada nim 404 na szczegóły sprawy —
 * `fetchInstanceDetail`). Serwer tak odpowiada, gdy sprawy nie ma ALBO pytający nie ma do niej
 * podglądu (prawo `processes.instances.view` w firmie rekordu) i nie może już na niej decydować —
 * nie ujawnia wtedy istnienia sprawy. Dla okna decyzji otwartego z listy i dla karty rekordu znaczy
 * to tyle, że sprawa nie czeka już na decyzję tej osoby; ponowienie dostanie tę samą odpowiedź.
 */
export function isInstanceNotFoundError(err: unknown): boolean {
  return extractErrorCode(err) === INSTANCE_NOT_FOUND_CODE;
}

// ============================================================================
// F.4 (ADR-0073) — podgląd puli akceptantów węzła docelowego (wskazanie adresata)
// ============================================================================

/** Pozycja puli akceptantów — bez maili (privacy, patrz kontrakt ADR-0073). */
export interface PoolApprover {
  id: string;
  displayName: string;
}

/**
 * `GET /api/processes/instances/:id/pool?nodeId=` — pula akceptantów DANEGO węzła
 * decyzyjnego instancji (rozwiązana z zamrożonego snapshotu). Listę dostaje wyłącznie osoba,
 * która może TERAZ decydować w którymś otwartym kroku tej sprawy — jest w puli kroku albo jest
 * eskalowanym przełożonym spoza puli, i jeszcze nie oddała głosu (backend 403 `NOT_ASSIGNED`
 * inaczej). Firma pochodzi ze sprawy, nie z nagłówka `X-Company-Id` — wywołanie działa też
 * w trybie „Wszystkie firmy" (wymagany dostęp do firmy sprawy, inaczej 404).
 */
export async function fetchDecisionPool(
  instanceId: string,
  nodeId: string,
): Promise<PoolApprover[]> {
  const res = await api.get<{ approvers: PoolApprover[] }>(
    `/processes/instances/${instanceId}/pool`,
    { params: { nodeId } },
  );
  return res.data.approvers;
}

/** Węzeł decyzyjny osiągalny ze startu procesu — pula + czy wymaga wskazania adresata. */
export interface StartPoolNode {
  nodeId: string;
  requiresDesignation: boolean;
  defaultAddresseeUserId: string | null;
  approvers: PoolApprover[];
}

/**
 * `GET /api/processes/:id/start-pool?targetRecordId=` — węzły decyzyjne, do których
 * prowadzi start procesu (żywy graf, NIE zamrożony snapshot — instancja jeszcze nie
 * istnieje). ZAŁOŻENIE (flagowane w raporcie oddania): kontrakt nie precyzuje koperty
 * odpowiedzi — przyjęto `{ nodes: [...] }` jako najprostszy kształt zgodny z opisem
 * "dla każdego węzła... zwraca {...}".
 */
export async function fetchStartPool(
  processId: string,
  targetRecordId: string,
): Promise<StartPoolNode[]> {
  const res = await api.get<{ nodes: StartPoolNode[] }>(`/processes/${processId}/start-pool`, {
    params: { targetRecordId },
  });
  return res.data.nodes;
}

// ============================================================================
// Akcje: start / decyzja / anuluj
// ============================================================================

export interface StartProcessResult {
  instanceId: string;
  instanceNumber: number;
}

export async function startProcess(
  processId: string,
  body: {
    targetRecordId: string;
    initiatorComment?: string;
    /** F.4 — wskazanie adresata per węzeł wymagający designacji (start procesu). */
    addressees?: Array<{ nodeId: string; userId: string }>;
    /** Priorytet/pilność wniosku — opcjonalny, backend domyślnie `DEFAULT_DECISION_PRIORITY`. */
    priority?: DecisionPriority;
    /** Termin (SLA, migracja 0126) — ISO datetime string; pominięte/`null` = bez terminu. */
    deadlineAt?: string | null;
  },
): Promise<StartProcessResult> {
  const res = await api.post<StartProcessResult>(`/processes/${processId}/start`, body);
  return res.data;
}

export interface SubmitDecisionResult {
  stepResult: string;
  instanceStatus: InstanceStatus;
}

/** Decyzja przez drogę (`approve` + `routeKey`) albo sam komentarz. */
export interface RouteDecisionPayload {
  decisionType: Exclude<DecisionType, 'step_back'>;
  comment?: string;
  /** Wybrana droga (id krawędzi z `InstanceStep.availableRoutes`); wymagana przy ≥ 2 drogach. */
  routeKey?: string;
  /** F.4 — wskazanie adresata dla węzła(-łów) aktywowanych tą decyzją (max 20 wg kontraktu). */
  nextAddressees?: Array<{ nodeId: string; userId: string }>;
  /**
   * F.5 (ADR-0074) — potwierdzenie miękkiego ostrzeżenia procedury kontrolnej
   * (`CONTROL_WARNING`, 409). Silnik re-waliduje przy ponownym submicie i, gdy tenant
   * potwierdził, przepuszcza ostrzeżenie zamiast blokować. Patrz `useControlWarning`.
   */
  warningsAccepted?: boolean;
}

/**
 * „Cofnij do poprzedniego etapu": komentarz obowiązkowy — pusty po przycięciu spacji odrzuca
 * silnik kodem `STEP_BACK_COMMENT_REQUIRED` (422; walidator body go nie wyprzedza, mapowanie
 * na komunikat: `approvals-api`). Bez drogi, adresata i potwierdzania ostrzeżeń — te należą do dróg.
 */
export interface StepBackDecisionPayload {
  decisionType: 'step_back';
  comment: string;
}

export type SubmitDecisionPayload = RouteDecisionPayload | StepBackDecisionPayload;

export async function submitDecision(
  stepInstanceId: string,
  body: SubmitDecisionPayload,
): Promise<SubmitDecisionResult> {
  const res = await api.post<SubmitDecisionResult>(`/processes/decisions/${stepInstanceId}`, body);
  return res.data;
}

export interface CancelInstanceResult {
  instanceId: string;
  status: InstanceStatus;
  idempotent: boolean;
}

export async function cancelInstance(
  instanceId: string,
  body: { reason?: string } = {},
): Promise<CancelInstanceResult> {
  const res = await api.post<CancelInstanceResult>(
    `/processes/instances/${instanceId}/cancel`,
    body,
  );
  return res.data;
}
